import { BluAsyncDialog } from '@bludata/components';
import axios from 'axios';
import type { FilterObject, SortObject } from '@bludata/hook-usetableloader';

const networkErrorString = 'Errore di connessione al server.';

/**
 *
 * @param tablename Nome della tabellata
 * @param id id del record che si desidera ottenere
 * @param property Proprietà per cui filtrare il record
 * @returns la classica struttura per la chiamata tabella con all'interno un record o nessun record se il record non viene trovato
 */
export const loadTableItem = <T,>(
  tablename: string,
  id: any,
  property?: string,
) => {
  return axios.get<{
    data: T[];
  }>(`/focusapi/tabella/${tablename}`, {
    params: {
      filter: JSON.stringify([
        {
          property: property || 'id',
          operator: '=',
          value: id,
        },
      ]),
    },
  });
};

/**
 *
 * @param tablename Nome della tabella
 * @returns Ritorna un oggetto di tipo T, che contiene i valori default per quella tabella
 */
export const loadTableNewRecord = <T,>(tablename: string) => {
  return axios.get<T>('/focusapi/getnewrecord', {
    params: {
      _dc: new Date().getTime(),
      tablename,
    },
  });
};

/**
 *
 * @param tablename Nome della tabella
 * @param id Id del record da eliminare dalla tabella
 * @returns classica struttura tabella con success true all'interno ed eventuale messaggio di errore
 */
export const deleteTableItem = (tablename: string, id: number | string) => {
  return axios.delete(`/focusapi/tabella/${tablename}?id=${id}`);
};

/**
 *
 * @param tablename Nome della tabella
 * @param data Oggetto di tipo T (tabella table interface) che si desidera creare (ATTENZIONE! NON MANDATE UN ID O UN CODICE FILIALE)
 * @returns il record aggiunto dentro la classica struttura di result della chiamata tabella
 */
export const createTableItem = <T,>(tablename: string, data: T) => {
  return axios<{ data: T[]; success: boolean }>({
    method: 'post',
    url: `/focusapi/tabella/${tablename}`,
    data: {
      data: JSON.stringify([data]),
    },
  });
};

/**
 *
 * @param tablename Nome della tabella
 * @param data Oggetto di tipo T (tabella table interface) che si desidera modificare (ATTENZIONE! MANDATE UN ID), non è necessario mandare tutti i campi
 * @returns Il record modificato all'interno della classica struttura tabella
 */
export const editTableItem = <T,>(tablename: string, data: T) => {
  return axios<{ data: T[]; success: boolean }>({
    method: 'put',
    url: `/focusapi/tabella/${tablename}`,
    data: {
      data: JSON.stringify([data]),
    },
  });
};

/**
 *
 * @param tablename Nome della tabella
 * @param data Oggetto di tipo T (tabella table interface) che si desidera creare o modificare
 * @param isNew boolean che determina se andare in POST o in PUT
 * @returns Classica struttura di risposta del servizio tabella con all'interno il record creato o modificato
 */
export const unifiedCreateOrEditTableItem = <T,>(
  tablename: string,
  data: T,
  isNew?: boolean,
) => {
  return !isNew
    ? editTableItem<T>(tablename, data)
    : createTableItem<T>(tablename, data);
};

/**
 *
 * @param tablename Nome della tabella
 * @param id Id del record da bloccare
 * @returns true se il record è stato bloccato
 */
export const lockRecord = (tablename: string, id: number) => {
  return new Promise<boolean>((resolve, reject) => {
    axios
      .get<boolean>('/focusapi/lockrecord', {
        params: {
          tablename,
          id,
        },
      })
      .then((response) => {
        if (response.data) {
          resolve(true);
        } else {
          resolve(false);
        }
      })
      .catch(() => {
        reject();
      });
  });
};

/**
 *
 * @param tablename Nome della tabella
 * @param id Id del record da togliere dal lockrecord
 * @returns true se il record è stato bloccato
 */
export const unlockRecord = (tablename: string, id: number) => {
  return new Promise<boolean>((resolve, reject) => {
    axios
      .get<boolean>('/focusapi/unlockrecord', {
        params: {
          tablename,
          id,
        },
      })
      .then((response) => {
        //console.log(response.data, typeof response.data);
        if (response.data) {
          resolve(true);
        } else {
          reject();
        }
      })
      .catch(() => {
        reject();
      });
  });
};
/**
 * Carica dati da una tabella tramite l'API tabella (GET /tabella/{tablename}).
 * Alternativa non-hook a useTableLoader, utile in funzioni async o fuori dal ciclo di vita React.
 *
 * @param tablename Nome della tabella
 * @param options Opzioni di filtro, ordinamento, paginazione, canale API e AbortSignal
 * @returns Oggetto con data (array di record), total (conteggio totale) e success
 */
export const loadTableData = async <T,>(
  tablename: string,
  options?: {
    filters?: FilterObject[];
    sort?: SortObject[];
    page?: number;
    start?: number;
    limit?: number;
    api?: string;
    signal?: AbortSignal;
  },
): Promise<{ data: T[]; total: number; success: boolean }> => {
  const {
    filters = [],
    sort = [],
    page = 0,
    start = 0,
    limit = 99999999,
    api = 'focusapi',
    signal,
  } = options || {};

  const response = await axios.get<{
    data: T[];
    total: number;
    success: boolean;
  }>(`/${api}/tabella/${tablename}`, {
    params: {
      _dc: new Date().getTime(),
      filter: JSON.stringify(filters),
      sort: JSON.stringify(sort),
      page,
      start,
      limit,
    },
    signal,
  });

  return {
    data: response.data.data ?? [],
    total: response.data.total ?? 0,
    success: response.data.success ?? true,
  };
};

/**
 * Funzione che mostra un dialogo di errore predefinito.
 * @param error Errore da visualizzare nel dialogo. Se non viene fornito, viene utilizzato il messaggio di errore di rete predefinito.
 * @param type Tipo di dialogo da visualizzare. Può essere "warning", "error" o "info". Se non viene fornito, viene utilizzato "error" come tipo predefinito.
 */
export const errorDialogDefaultFunction = (
  error?: string,
  type?: 'warning' | 'error' | 'info',
) => {
  BluAsyncDialog({
    title: 'Attenzione',
    message: error || networkErrorString,
    type: type || 'error',
    hideDeclineButton: true,
    confimButton: 'Chiudi',
  });
};
